import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Phone, Star, Award, Clock, Shield } from 'lucide-react';
import { Container } from '../../layout';
import { buttonVariants } from '../../ui/Button';
import { cn } from '../../../utils/cn';

/**
 * Trust indicator data displayed in the CTA section
 */
const trustIndicators = [
  {
    icon: Award,
    label: 'Licensed Therapists',
  },
  {
    icon: Star,
    label: '5-Star Rated',
  },
  {
    icon: Clock,
    label: 'Same-Day Booking',
  },
  {
    icon: Shield,
    label: 'Satisfaction Guaranteed',
  },
];

export interface BookingCTAProps {
  /**
   * Main headline text
   * @default "Ready to Experience True Relaxation?"
   */
  headline?: string;
  /**
   * Subheadline/supporting text
   * @default "Book your personalized massage therapy session today..."
   */
  subheadline?: string;
  /**
   * Primary CTA button text
   * @default "Book Now"
   */
  primaryButtonText?: string;
  /**
   * Primary CTA button link
   * @default "/book"
   */
  primaryButtonLink?: string;
  /**
   * Secondary CTA button text
   * @default "Contact Us"
   */
  secondaryButtonText?: string;
  /**
   * Secondary CTA button link
   * @default "/contact"
   */
  secondaryButtonLink?: string;
  /**
   * Whether to show trust indicators
   * @default true
   */
  showTrustIndicators?: boolean;
  /**
   * Whether to show the next available prompt
   * @default true
   */
  showAvailabilityPrompt?: boolean;
  /**
   * Optional additional className for the section
   */
  className?: string;
}

/**
 * BookingCTA component - A bold call-to-action section for the homepage
 *
 * Features:
 * - Full-width gradient background (teal to violet)
 * - Compelling headline and subheadline
 * - Two CTA buttons (primary white, secondary outlined)
 * - Trust indicators (Licensed, 5-star rated, etc.)
 * - Decorative background patterns
 * - Next available appointment prompt
 * - Fully responsive design
 *
 * @example
 * <BookingCTA />
 *
 * @example
 * <BookingCTA
 *   headline="Start Your Wellness Journey"
 *   primaryButtonText="Schedule Now"
 *   showTrustIndicators={false}
 * />
 */
export const BookingCTA: FC<BookingCTAProps> = ({
  headline = 'Ready to Experience True Relaxation?',
  subheadline = 'Book your personalized massage therapy session today and discover the healing power of professional therapeutic care. Your journey to wellness begins with a single step.',
  primaryButtonText = 'Book Now',
  primaryButtonLink = '/book',
  secondaryButtonText = 'Contact Us',
  secondaryButtonLink = '/contact',
  showTrustIndicators = true,
  showAvailabilityPrompt = true,
  className,
}) => {
  return (
    <section
      className={cn(
        'relative overflow-hidden',
        // Gradient background: teal to violet direction
        'bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-600',
        'py-16 md:py-20 lg:py-24',
        className
      )}
      aria-labelledby="booking-cta-heading"
    >
      {/* Decorative background patterns */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        {/* Large circle - top right */}
        <div
          className={cn('absolute -top-32 -right-32 w-96 h-96 rounded-full', 'bg-white/5 blur-3xl')}
        />
        {/* Medium circle - bottom left */}
        <div
          className={cn(
            'absolute -bottom-24 -left-24 w-80 h-80 rounded-full',
            'bg-secondary-400/10 blur-2xl'
          )}
        />
        {/* Small circle - center */}
        <div
          className={cn(
            'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
            'w-[600px] h-[600px] rounded-full',
            'bg-gradient-to-br from-white/5 to-transparent'
          )}
        />

        {/* Decorative dots pattern */}
        <div className="absolute top-12 left-16 w-2 h-2 bg-white/20 rounded-full" />
        <div className="absolute top-24 right-24 w-3 h-3 bg-white/15 rounded-full" />
        <div className="absolute bottom-20 left-1/3 w-2 h-2 bg-white/25 rounded-full" />
        <div className="absolute top-1/3 right-1/3 w-1.5 h-1.5 bg-white/20 rounded-full" />
        <div className="absolute bottom-12 right-16 w-2.5 h-2.5 bg-white/15 rounded-full" />

        {/* Wave pattern at bottom */}
        <svg
          className="absolute bottom-0 left-0 right-0 w-full h-12 text-white/5"
          viewBox="0 0 1440 48"
          fill="currentColor"
          preserveAspectRatio="none"
        >
          <path d="M0,24 C360,48 720,0 1080,24 C1260,36 1350,36 1440,24 L1440,48 L0,48 Z" />
        </svg>
      </div>

      <Container size="xl" className="relative z-10">
        <div className="text-center max-w-3xl mx-auto">
          {/* Next available appointment prompt */}
          {showAvailabilityPrompt && (
            <div
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 rounded-full',
                'bg-white/10 backdrop-blur-sm',
                'text-white/90 text-sm font-medium',
                'mb-6'
              )}
            >
              <span className={cn('w-2 h-2 rounded-full', 'bg-green-400 animate-pulse')} />
              Appointments available this week
            </div>
          )}

          {/* Headline */}
          <h2
            id="booking-cta-heading"
            className={cn(
              'font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl',
              'font-bold text-white leading-tight tracking-tight',
              'mb-6'
            )}
          >
            {headline}
          </h2>

          {/* Subheadline */}
          <p
            className={cn(
              'text-lg sm:text-xl text-white/80 leading-relaxed',
              'mb-8 max-w-2xl mx-auto'
            )}
          >
            {subheadline}
          </p>

          {/* CTA Buttons */}
          <div
            className={cn('flex flex-col sm:flex-row items-center justify-center gap-4', 'mb-10')}
          >
            {/* Primary Button - White/Light on colored background */}
            <Link
              to={primaryButtonLink}
              className={cn(
                buttonVariants({ size: 'lg' }),
                'bg-white text-primary-700',
                'hover:bg-gray-100 dark:hover:bg-gray-200 active:bg-gray-200 dark:active:bg-gray-300',
                'shadow-lg shadow-black/20',
                'hover:shadow-xl hover:shadow-black/25',
                'transition-all duration-200',
                'min-w-[180px]'
              )}
            >
              <Calendar className="w-5 h-5" aria-hidden="true" />
              {primaryButtonText}
            </Link>

            {/* Secondary Button - Outlined */}
            <Link
              to={secondaryButtonLink}
              className={cn(
                buttonVariants({ size: 'lg' }),
                'bg-transparent',
                'border-2 border-white/70 text-white',
                'hover:bg-white/10 hover:border-white',
                'active:bg-white/20',
                'transition-all duration-200',
                'min-w-[180px]'
              )}
            >
              <Phone className="w-5 h-5" aria-hidden="true" />
              {secondaryButtonText}
            </Link>
          </div>

          {/* Trust Indicators */}
          {showTrustIndicators && (
            <div
              className={cn(
                'backdrop-blur-sm bg-white/5 rounded-2xl py-6 px-8',
                'flex flex-wrap items-center justify-center gap-6 md:gap-8'
              )}
            >
              {trustIndicators.map((indicator) => {
                const IconComponent = indicator.icon;
                return (
                  <div key={indicator.label} className="flex items-center gap-2 text-white/80">
                    <IconComponent className="w-5 h-5 text-white/60" aria-hidden="true" />
                    <span className="text-sm font-medium">{indicator.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};

BookingCTA.displayName = 'BookingCTA';
